import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';
import type { PreviewUrl } from '@src/domain/pascos/value_objects/preview_url';

/**
 * Hook: useGetPascoPreviewUrl
 * Fetches a presigned URL for previewing a PASCO file (JPG image)
 * 
 * @param pascoId - The unique identifier of the PASCO
 * @param options - React Query options
 * @returns Query result with preview URL
 * 
 * @example
 * ```typescript
 * const { data: previewUrl, isLoading } = useGetPascoPreviewUrl(pascoId, {
 *     enabled: !!pascoId,
 *     staleTime: 5 * 60 * 1000, // 5 minutes
 * });
 * 
 * if (previewUrl) {
 *     console.log(previewUrl.previewUrl); // Use for image display
 * }
 * ```
 */
export function useGetPascoPreviewUrl(
    pascoId: string | undefined,
    options?: Omit<
        UseQueryOptions<PreviewUrl, Error>,
        'queryKey' | 'queryFn'
    >
) {
    const pascoService = getPascoService();

    return useQuery({
        queryKey: ['pascos', pascoId, 'preview-url'],
        queryFn: async () => {
            if (!pascoId) {
                throw new Error('Pasco ID is required');
            }
            return await pascoService.getPreviewUrl(pascoId);
        },
        enabled: !!pascoId && (options?.enabled ?? true),
        staleTime: 5 * 60 * 1000, // 5 minutes - URLs are valid for longer but we refresh to be safe
        gcTime: 10 * 60 * 1000, // 10 minutes - Cache for 10 minutes
        retry: 2, // Retry once on failure
        ...options,
    });
}
